import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { Field, Fieldset } from "@headlessui/react";
import { Link } from "react-router-dom";
import { ExternalLinkIcon } from "@heroicons/react/outline";
import Divider from "../../Divider";
import {
  addLocalNotification,
  applyDeposit,
  clearInvoiceTransaction,
  clearInvoiceTransactionError,
  requestCreateInvoiceTransaction,
  listDeposits,
  refreshAccountingTransaction,
  requestListAccount,
  requestListBusinessPartner,
  requestListBusinessProduct,
  requestListBusinessTax,
  requestUpdateInvoiceTransaction,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useBusinessProductDispatch,
  useBusinessProductState,
  useBusinessTaxDispatch,
  useBusinessTaxState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
  usePeriodLock,
} from "../../../../Context";
import SlideOver from "../../Modal/SlideOver";
import PeriodClosedNotice from "../PeriodClosedNotice";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import Button from "../../Button";
import FormSelect from "../../Form/FormSelect";
import FormDate from "../../Form/FormDate";
import ProductEntryTable from "../../Table/ProductEntryTable";
import FormError from "../../Form/FormError";
import { Alert } from "../../../Alert";
import {
  cancelAPIRequest,
  currencyFormat,
  formatCurrency,
  getPeriodClosedError,
} from "../../../../Utils";
import {
  addMonths,
  fromPeriod,
  isPeriod,
  toPeriod,
} from "../../../../Utils/calendarDate";
import {
  buildPostedTaxes,
  buildSelectedTaxes,
  calculateFinalTotal,
  WHOLE_RUPIAH,
  describeTaxRow,
  DISCOUNT_OPTIONS,
  handleDiscountChange,
  transformInvoiceItems,
} from "../../../../Utils/AccountingUtil";
import FormInput from "../../Form/FormInput";
import FormLabel from "../../Form/FormLabel";
import InitAccountPromptModal from "../InitAccountPromptModal";
import DepositAllocationList from "../DepositAllocationList";
import {
  buildDefaultAllocations,
  isDepositOpen,
  sortDepositsOldestFirst,
  sumAllocations,
  validateAllocations,
} from "../../../../Utils/DepositUtil";
import { getStockConsumedMessage } from "../../../../Utils/PaymentUtils";
import { toWibPeriod } from "../../../../Utils/PeriodUtils";

const InvoiceModal = ({
  open,
  onClose,
  invoice,
  transactionType = "INCOME_RECEIVABLE",
}) => {
  const { t, i18n } = useTranslation();

  // EXPENSE_PAYABLE = purchase invoice (Bill); INCOME_RECEIVABLE = sales invoice.
  // When editing, the record's own type wins so the modal can never flip an
  // existing transaction's direction.
  const isPurchase = invoice?.type
    ? invoice.type === "EXPENSE_PAYABLE"
    : transactionType === "EXPENSE_PAYABLE";

  const businessDispatch = useBusinessTransactionDispatch();
  const productDispatch = useBusinessProductDispatch();
  const accountingDispatch = useAccountingDispatch();
  const notificationDispatch = useNotificationDispatch();
  const taxDispatch = useBusinessTaxDispatch();

  const [selectedDiscount, setSelectedDiscount] = useState(DISCOUNT_OPTIONS[2]);
  const [discount, setDiscount] = useState("");
  // Preserved from an existing invoice so editing never silently flips an
  // after-tax discount to before-tax (the UI has no toggle for it yet).
  const [applyAfterTax, setApplyAfterTax] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [date, setDate] = useState(new Date());
  const [dueDate, setdueDate] = useState(new Date());
  const [trackInventory, setTrackInventory] = useState(false);
  const [ignoreStockValidation, setIgnoreStockValidation] = useState(false);
  const [errors, setErrors] = useState({});
  // Every row carries selectedTaxes from the start, including the empty list
  // on a fresh one. That is what makes the save always state the line's taxes:
  // a PUT that leaves the key out tells the backend to keep what it stored, so
  // a row that never set it could never have its last tax removed.
  const [productItems, setProductItems] = useState([
    {
      product: "",
      quantity: 1,
      tax: "",
      total: 0,
      selectedTaxes: [],
    },
  ]);

  const { businessPartners, businessPartnerLoading, businessPartnerError } =
    useBusinessTransactionState();
  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);
  const {
    minDate,
    isDateClosed,
    isClosed,
    closedThroughLabel,
    refetch: refetchPeriod,
  } = usePeriodLock(user?.mainBusiness?.id, { locale: i18n.language });
  const dateLocked = isDateClosed(date);
  const { businessProducts, businessProductsLoading, businessProductsError } =
    useBusinessProductState();
  const {
    request,
    newInvoiceID,
    newInvoiceError,
    newInvoiceLoading,
    invoiceID,
    submitLoading,
    submitError,
    accounts,
    accountLoading,
    accountError,
    allAccountLoading,
  } = useAccountingState();
  const { businessTaxes, businessTaxesLoading, businessTaxesError } =
    useBusinessTaxState();

  const isEdit = Boolean(invoice);
  // Every new invoice is saved in whole rupiah; an edit keeps the arithmetic
  // the invoice was posted with, so opening an old one never moves its PPN.
  const taxRounding = isEdit ? (invoice?.taxRounding ?? null) : WHOLE_RUPIAH;

  // A 409 STOCK_CONSUMED is about the line prices, so it is shown beside the
  // product list rather than in the generic error box. Nothing in the form is
  // reset: the user puts the old price back and saves the rest.
  const stockConsumedMessage = isEdit
    ? getStockConsumedMessage(submitError)
    : null;

  // W8 · a new sales invoice can use the customer's deposits straight away.
  // The invoice is created first and each deposit applied after it, so the
  // invoice total and its posting never change because of a deposit.
  const offersDeposits = !isEdit && !isPurchase;
  const [partnerDeposits, setPartnerDeposits] = useState([]);
  const [useDeposits, setUseDeposits] = useState(true);
  const [depositAllocations, setDepositAllocations] = useState({});
  // Until the user edits an amount, the allocation follows the total.
  const [depositsTouched, setDepositsTouched] = useState(false);
  const [applyingDeposits, setApplyingDeposits] = useState(false);
  const [showDepositErrors, setShowDepositErrors] = useState(false);
  // UU PPN Pasal 9 ayat (9): an input VAT invoice may be credited in its own
  // month or in any of the next three. Empty means the invoice month.
  const [vatCreditPeriod, setVatCreditPeriod] = useState(null);
  const totalLoading = businessTaxesLoading || businessProductsLoading;

  // Partner, product and tax are all dropdowns, which fire no input event —
  // SlideOver's fallback detection alone would let Escape bin a filled invoice.
  const dirty =
    isEdit ||
    Boolean(selectedPartner || discount || trackInventory || vatCreditPeriod) ||
    productItems.some(
      (item) => item.product || item.tax || item.price?.floatValue,
    );

  // A tax period is a WIB calendar month, the same as Rekap PPN and the server's
  // check on `vat_credit_period`. Reading the month off the UTC ISO string put a
  // bill dated the 1st before 07:00 WIB in the previous month, offering Oct–Jan
  // for 1 Nov 05:00 instead of Nov–Feb.
  const invoicePeriod = useMemo(() => toWibPeriod(date), [date]);
  const maxCreditPeriod = useMemo(
    () => (invoicePeriod ? addMonths(invoicePeriod, 3) : null),
    [invoicePeriod],
  );

  // Generate options for parners
  const partnerOptions = useMemo(
    () =>
      businessPartners.map((item) => ({
        ...item,
        value: item.id,
        label: item.name,
      })),
    [businessPartners],
  );

  // A Bill may only use purchase (PPN Masukan) taxes; a sale only sales taxes.
  // Legacy untyped taxes are treated as sales (prior behaviour).
  const availableTaxes = useMemo(
    () =>
      (businessTaxes || []).filter((tx) =>
        isPurchase ? tx.type === "PURCHASES" : tx.type !== "PURCHASES",
      ),
    [businessTaxes, isPurchase],
  );

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      cancelAPIRequest(request);
      clearInvoiceTransaction(accountingDispatch);
      clearInvoiceTransactionError(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Init-account guard: a business without a chart of accounts cannot post,
  // so offer the skippable init flow before showing the create form.
  const [initSkipped, setInitSkipped] = useState(false);
  // Latched probe result — see TransactionModal: `accounts` is shared state and
  // is reset to [] by any other in-flight list request, so reading it directly
  // makes the prompt flash on and off. null = not decided yet.
  const [chartEmpty, setChartEmpty] = useState(null);
  const listSeenLoading = useRef(false);

  useEffect(() => {
    if (open && !isEdit && user?.mainBusiness?.id) {
      listSeenLoading.current = false;
      setChartEmpty(null);
      requestListAccount(accountingDispatch, {
        businessID: user.mainBusiness.id,
        limit: 1,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, user?.mainBusiness?.id]);

  useEffect(() => {
    if (!open || isEdit) return;
    if (accountLoading || allAccountLoading) {
      listSeenLoading.current = true;
      return;
    }
    if (!listSeenLoading.current || accountError) return;
    if (accounts.length > 0) setChartEmpty(false);
    else setChartEmpty((prev) => (prev === null ? true : prev));
  }, [
    open,
    isEdit,
    accountLoading,
    allAccountLoading,
    accountError,
    accounts.length,
  ]);

  const showInitPrompt = open && !isEdit && !initSkipped && chartEmpty === true;

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      requestListBusinessPartner(businessDispatch, {
        businessID: user?.mainBusiness?.id,
      });
    }
  }, [user?.mainBusiness?.id, businessDispatch]);

  useEffect(() => {
    onLoad();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.mainBusiness?.id]);

  // Populate form when editing
  // The prefill also runs on partnerOptions and businessProducts, so any
  // refetch of those lists used to rebuild every row from the invoice and throw
  // away whatever the user had changed. Filling the form once per invoice is
  // enough — the lists only supply labels, which the pickers resolve at render
  // time.
  const prefilledRef = useRef(null);
  useEffect(() => {
    if (invoice && prefilledRef.current !== invoice.id) {
      prefilledRef.current = invoice.id;
      setDate(new Date(invoice.createdAt) || "");
      setdueDate(new Date(invoice?.paymentDueAt) || "");
      // Legacy invoices may have trackInventory: null; fall back to deductInventory.
      setTrackInventory(invoice.trackInventory ?? invoice.deductInventory ?? false);
      setSelectedPartner(
        partnerOptions.find((partner) => partner.id === invoice?.partner?.id) ||
          null,
      );
      if (invoice.items?.items && invoice.items?.items?.length > 0) {
        const mappedItems = invoice.items.items.map((item) => {
          return {
            ...item,
            // Nullable relation: the closed period guard keeps this invoice
            // intact after its product is deleted, so the line still carries
            // productID while `product` resolves to null. The spread also
            // carries taxID and the posted taxComputation/taxRate/taxAccountID,
            // which resolveItemTax reads.
            product: item.product
              ? {
                  ...item.product,
                  label: item.product.name,
                  value: item.product.id,
                  price: item.price,
                }
              : null,
            quantity: item.quantity || 1,
            price: { floatValue: item.price || 0, value: item.price || 0 },
            total: { floatValue: item.price || 0 },
            tax: item.tax
              ? {
                  ...item.tax,
                  label: item.tax.name,
                  value: item.tax.id,
                }
              : "",
            // The terms every tax on this line was posted with, written once
            // and never touched again: removing a tax from the line and
            // putting it back has to charge the posted rate, not today's.
            postedTaxes: buildPostedTaxes(item),
            selectedTaxes: buildSelectedTaxes(item, availableTaxes),
          };
        });
        setProductItems(mappedItems);
      }
      setVatCreditPeriod(
        isPeriod(invoice.vatCreditPeriod) ? invoice.vatCreditPeriod : null,
      );
      if (invoice.discount) {
        setSelectedDiscount(
          invoice.discount.type === "PERCENTAGE"
            ? DISCOUNT_OPTIONS[0]
            : DISCOUNT_OPTIONS[1],
        );
        setDiscount(invoice.discount.value);
        setApplyAfterTax(invoice.discount.applyAfterTax || false);
      } else {
        setSelectedDiscount(DISCOUNT_OPTIONS[2]);
        setApplyAfterTax(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, partnerOptions, businessProducts]);

  // The prefill above runs once, the moment the invoice arrives, and the
  // partner list usually has not loaded yet — so it set the partner to null
  // and saving failed with "Rekan atau pelanggan diperlukan." on an invoice
  // that plainly showed one. Fill it in when the list catches up, and only
  // while nothing is selected, so a partner the user picked or changed is
  // never overwritten.
  useEffect(() => {
    if (invoice && !selectedPartner && partnerOptions.length) {
      setSelectedPartner(
        partnerOptions.find((partner) => partner.id === invoice?.partner?.id) ||
          null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, partnerOptions]);

  //  Handle create success
  const handledInvoiceID = useRef(null);
  useEffect(() => {
    if (!newInvoiceID || isEdit || handledInvoiceID.current === newInvoiceID) {
      return;
    }
    handledInvoiceID.current = newInvoiceID;

    const finish = (depositsFailed) => {
      // Close modal
      onClose();
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `create-invoice-${newInvoiceID}`,
        type: "success",
        title: "common.success",
        body: isPurchase
          ? "bills.notifications.createSuccess"
          : "invoices.notifications.createSuccess",
        showFull: true,
      });
      if (depositsFailed) {
        // The invoice stands either way; the deposits can still be applied
        // from its detail.
        addLocalNotification(notificationDispatch, {
          id: `create-invoice-deposits-${newInvoiceID}`,
          type: "warning",
          title: "common.errors.label",
          body: "deposits.notifications.applyAfterCreateFailed",
          showFull: true,
        });
      }
    };

    const toApply = useDeposits
      ? sortDepositsOldestFirst(partnerDeposits).filter(
          (deposit) => (depositAllocations[deposit.id] || 0) > 0,
        )
      : [];
    if (!offersDeposits || toApply.length === 0) {
      finish(false);
      return;
    }

    setApplyingDeposits(true);
    (async () => {
      let failed = false;
      for (const deposit of toApply) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await applyDeposit(deposit.id, {
            invoiceID: newInvoiceID,
            amount: Math.round(depositAllocations[deposit.id]),
          });
        } catch (error) {
          failed = true;
          break;
        }
      }
      // The list row was read before the deposits were applied; its payments
      // and Sisa Pembayaran must include them when the detail is opened.
      try {
        await refreshAccountingTransaction(accountingDispatch, newInvoiceID);
      } catch (error) {
        // The page's next list load catches up.
      }
      setApplyingDeposits(false);
      finish(failed);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationDispatch, newInvoiceID, onClose, isEdit, isPurchase]);

  //  Handle update success
  useEffect(() => {
    if (invoiceID && isEdit) {
      // Close modal
      onClose();
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `edit-invoice-${invoiceID}`,
        type: "success",
        title: "common.success",
        body: isPurchase
          ? "bills.notifications.updateSuccess"
          : "invoices.notifications.updateSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, invoiceID, onClose, isEdit, isPurchase]);

  useEffect(() => {
    if (selectedDiscount?.value === "noDiscount") {
      setDiscount("");
    }
  }, [selectedDiscount?.value]);

  // Moving the bill's date can push an already-chosen credit period outside the
  // window. Clear it and say so rather than submit a value the server rejects.
  useEffect(() => {
    if (!isPurchase || !vatCreditPeriod || !invoicePeriod || !maxCreditPeriod) {
      return;
    }
    if (vatCreditPeriod < invoicePeriod || vatCreditPeriod > maxCreditPeriod) {
      setVatCreditPeriod(null);
      setErrors((prev) => ({
        ...prev,
        vatCreditPeriod: "bills.errors.vatCreditPeriodReset",
      }));
    }
  }, [isPurchase, invoicePeriod, maxCreditPeriod, vatCreditPeriod]);

  const onLoad = () => {
    if (user?.mainBusiness?.id) {
      requestListBusinessTax(taxDispatch, {
        businessID: user?.mainBusiness?.id,
      });

      requestListBusinessProduct(productDispatch, {
        businessID: user?.mainBusiness?.id,
      });
    }
  };

  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const handleFieldChange = (setter, field) => (eventOrValue) => {
    const value =
      eventOrValue?.target !== undefined
        ? eventOrValue.target.value
        : eventOrValue;
    setter(value);
    clearError(field);
  };

  const validate = () => {
    const newErrors = {};

    if (!selectedPartner?.value) {
      newErrors.selectedPartner = "invoices.errors.partner";
    }

    if (!date) {
      newErrors.date = "invoices.errors.dateRequired";
    }

    if (!dueDate) {
      newErrors.dueDate = "invoices.errors.dueDateRequired";
    }

    if (selectedDiscount?.value !== "noDiscount" && !discount) {
      newErrors.discount = "invoices.errors.discountRequired";
    }

    // Belt and braces: the picker already constrains this, but a value loaded
    // from an existing bill can sit outside the window.
    if (isPurchase && vatCreditPeriod) {
      if (
        !isPeriod(vatCreditPeriod) ||
        !invoicePeriod ||
        vatCreditPeriod < invoicePeriod ||
        vatCreditPeriod > maxCreditPeriod
      ) {
        newErrors.vatCreditPeriod = "bills.errors.vatCreditPeriodRange";
      }
    }

    const productErrors = [];

    productItems.forEach((item, index) => {
      const itemErrors = [];

      if (!item.product?.id) {
        itemErrors.push("invoices.table.error.product");
      }

      if (!item.quantity || item.quantity < 1 || item.quantity === "") {
        itemErrors.push("invoices.table.error.quantity");
      }

      const itemPrice = item.price?.floatValue ?? item.product?.price;
      if (!itemPrice || itemPrice <= 0) {
        itemErrors.push("invoices.table.error.price");
      }

      if (itemErrors.length > 0) {
        productErrors[index] = itemErrors;
      }
    });

    if (productErrors.length > 0) {
      newErrors.productItems = productErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (depositBoxShown && useDeposits) {
      setShowDepositErrors(true);
      if (Object.keys(depositErrors).length > 0) return;
    }
    // Guards the Enter-key path, which bypasses the disabled submit button.
    if (dateLocked) return;

    saveInvoice();
  };

  const saveInvoice = () => {
    const items = transformInvoiceItems(productItems);

    const input = {
      id: isEdit ? invoice?.id : undefined,
      business_id: user.mainBusiness.id,
      created_at: date.toISOString(),
      partner_id: selectedPartner?.value,
      payment_due_at: dueDate?.toISOString(),
      items,
      type: isEdit && invoice?.type ? invoice.type : transactionType,
      track_inventory: trackInventory,
      ...(!isEdit && { ignore_low_stock: ignoreStockValidation }),
      // Bills only — the server answers 400 for a sales invoice. On update an
      // omitted key keeps the stored value and an explicit null clears it, so
      // create sends it only when set while edit always sends a decision.
      ...(isPurchase &&
        (isEdit
          ? { vat_credit_period: vatCreditPeriod || null }
          : vatCreditPeriod && { vat_credit_period: vatCreditPeriod })),
      ...(selectedDiscount?.value !== "noDiscount" &&
        discount && {
          discount: {
            type:
              selectedDiscount.value === "percentage" ? "PERCENTAGE" : "AMOUNT",
            value: parseFloat(discount),
            ...(applyAfterTax && { applyAfterTax: true }),
          },
        }),
    };

    const action = isEdit
      ? requestUpdateInvoiceTransaction
      : requestCreateInvoiceTransaction;
    action(accountingDispatch, { input });
  };

  const updateProductItem = (index, updatedItem) => {
    const newProductItems = [...productItems];
    newProductItems[index] = { ...newProductItems[index], ...updatedItem };
    return newProductItems;
  };

  const handleAddRow = () => {
    setProductItems([
      ...productItems,
      {
        product: "",
        quantity: 1,
        tax: "",
        total: { floatValue: 0 },
        selectedTaxes: [],
      },
    ]);
  };

  const handleRemoveRow = (index) => {
    const newProductItems = productItems.filter((_, i) => i !== index);
    setProductItems(newProductItems);
  };

  const onSelectDiscont = (option) => {
    setSelectedDiscount(option);
    setDiscount("");
  };

  const handleChangeDiscount = (e) => {
    handleDiscountChange(e.target.value, selectedDiscount, setDiscount);
  };

  // A line can point at a product or a tax this form cannot show: a closed
  // period hides both, and deleting a tax now leaves the relation null on an
  // open invoice too. Either way the id stays on the line so the save keeps
  // the relation instead of silently stripping it, and the row would otherwise
  // read as a blank picker with no explanation.
  //
  // The check reads the picker's own list rather than item.taxID, which is now
  // only a mirror of the first tax that is not withheld — a line carrying just
  // a PPh 23 has no taxID at all.
  const unresolvedLines = useMemo(() => {
    // While the tax list is still loading every chip is unnamed, which is not
    // the same as a tax that is gone.
    if (businessTaxesLoading) return [];
    const known = new Set(availableTaxes.map((tax) => tax.id));

    return productItems.reduce((lines, item, index) => {
      const missingProduct = Boolean(item.productID) && !item.product;
      const missingTax = Array.isArray(item.selectedTaxes)
        ? item.selectedTaxes.some(
            (tax) => !known.has(tax?.id ?? tax?.value) && !tax?.name,
          )
        : Boolean(item.taxID) && !(item.tax && typeof item.tax === "object");
      if (missingProduct || missingTax) lines.push(index + 1);
      return lines;
    }, []);
  }, [productItems, availableTaxes, businessTaxesLoading]);

  // Names only. A line that carries a tax the picker filters out — a sales
  // tax on a bill, or one that was deleted — still charges the terms it was
  // posted with; this just stops the summary calling it "Pajak tidak
  // tersedia" while the tax is sitting in the business's list.
  const taxDirectory = useMemo(
    () => new Map((businessTaxes || []).map((tax) => [tax.id, tax])),
    [businessTaxes],
  );

  // Update the total calculations
  const calculations = useMemo(() => {
    return calculateFinalTotal(
      productItems,
      selectedDiscount?.value,
      discount,
      applyAfterTax,
      taxDirectory,
      taxRounding,
    );
  }, [
    productItems,
    selectedDiscount,
    discount,
    applyAfterTax,
    taxDirectory,
    taxRounding,
  ]);

  // Replace the current subtotal calculation
  const taxes = calculations.taxes;
  const subtotal = calculations.subtotal;
  const discountAmount = calculations.discount;
  const totalWithTax = calculations.grandTotal;
  const roundingAmount = calculations.roundingAmount;

  useEffect(() => {
    setPartnerDeposits([]);
    setDepositAllocations({});
    setDepositsTouched(false);
    setShowDepositErrors(false);
    const partnerID = selectedPartner?.value;
    if (!offersDeposits || !partnerID || !user?.mainBusiness?.id) return;
    let live = true;
    listDeposits({
      businessID: user.mainBusiness.id,
      partnerID,
      status: "open",
    })
      .then(({ items }) => {
        if (live) {
          const list = sortDepositsOldestFirst(items.filter(isDepositOpen));
          // Filled with the list, so the box never shows without its default.
          setPartnerDeposits(list);
          setDepositAllocations(
            buildDefaultAllocations(list, depositLimitRef.current),
          );
        }
      })
      .catch(() => {
        // No box is shown; the invoice itself is unaffected.
      });
    return () => {
      live = false;
    };
  }, [offersDeposits, selectedPartner?.value, user?.mainBusiness?.id]);

  // Whole rupiah, the unit the deposit endpoint settles in.
  const depositLimit = Math.max(0, Math.round(totalWithTax || 0));
  const depositLimitRef = useRef(depositLimit);
  depositLimitRef.current = depositLimit;
  useEffect(() => {
    if (!depositsTouched) {
      setDepositAllocations(buildDefaultAllocations(partnerDeposits, depositLimit));
    }
  }, [partnerDeposits, depositLimit, depositsTouched]);

  const depositBoxShown = offersDeposits && partnerDeposits.length > 0;
  const depositErrors = useMemo(
    () => validateAllocations(depositAllocations, partnerDeposits, depositLimit),
    [depositAllocations, partnerDeposits, depositLimit],
  );
  const depositUsed = useDeposits ? sumAllocations(depositAllocations) : 0;

  return (
    <>
    <SlideOver
      open={open}
      onClose={onClose}
      dirty={dirty}
      actionButton={
        <Link
          to={
            isPurchase
              ? isEdit
                ? `/purchase/edit/${invoice?.id}`
                : "/purchase/create"
              : isEdit
                ? `/invoice/edit/${invoice?.id}`
                : "/invoice/create"
          }
          className="rounded-md bg-white text-gray-400 hover:text-gray-500"
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalLinkIcon className="h-6 w-6" />
        </Link>
      }
      title={
        isPurchase
          ? isEdit
            ? "bills.modal.title.edit"
            : "bills.modal.title.add"
          : isEdit
            ? "invoices.modal.title.edit"
            : "invoices.modal.title.add"
      }
      footer={
        <div className="flex justify-between items-center">
          <div className="flex flex-col text-gray-900">
            <span className="text-sm">{t("invoices.fields.total")}</span>
            <div className="text-sm font-medium" data-testid="invoice-total">
              {totalLoading ? (
                <div className="animate-pulse h-4 mt-[2px] mb-[2px] w-20 bg-gray-300 rounded-md"></div>
              ) : (
                formatCurrency(totalWithTax || 0)
              )}
            </div>
          </div>
          <div className="flex space-x-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={
                writeBlocked ||
                dateLocked ||
                newInvoiceLoading ||
                submitLoading ||
                applyingDeposits
              }
              loading={newInvoiceLoading || submitLoading || applyingDeposits}
              type="submit"
              form="invoice-form"
              variant="primary"
            >
              {isEdit ? t("common.save") : t("common.create")}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id="invoice-form"
        className="space-y-4 mb-6"
        onSubmit={handleSubmit}
      >
        <WriteBlockGate>
        <PeriodClosedNotice error={submitError} onRefresh={refetchPeriod} />
        {unresolvedLines.length > 0 && (
          <Alert
            type="warning"
            title="invoices.warnings.unresolvedRelationsTitle"
            message={t("invoices.warnings.unresolvedRelations", {
              lines: unresolvedLines.join(", "),
            })}
          />
        )}
        <FormError
          error={
            getPeriodClosedError(submitError) || stockConsumedMessage
              ? newInvoiceError
              : newInvoiceError || submitError
          }
        />
        <Fieldset
          disabled={newInvoiceLoading || submitLoading}
          className="space-y-4 data-[disabled]:opacity-50"
        >
          <Field>
            <FormSelect
              required
              label={
                isPurchase ? "bills.fields.partner" : "invoices.fields.partner"
              }
              id="partner"
              name="partner"
              searchable
              options={partnerOptions}
              isLoading={businessPartnerLoading}
              value={selectedPartner}
              onChange={handleFieldChange(
                setSelectedPartner,
                "selectedPartner",
              )}
              placeholder={t(
                isPurchase
                  ? "bills.placeholders.partner"
                  : "invoices.placeholders.partner",
              )}
              error={businessPartnerError || errors.selectedPartner}
            />
          </Field>

          <Field>
            <FormDate
              required
              label="invoices.fields.date"
              name="date"
              selected={date}
              onChange={handleFieldChange(setDate, "date")}
              dateFormat="dd/MM/yyyy HH:mm"
              showTimeInput
              timeInputLabel={t("common.time")}
              minDate={minDate}
              isDateClosed={isDateClosed}
              placeholderText={t("invoices.placeholders.date")}
              error={errors.date}
            />
            {isClosed && (
              <p className="mt-1.5 text-xs text-amber-700">
                {dateLocked
                  ? t("accounting.period.hints.dateLocked", {
                      date: closedThroughLabel,
                    })
                  : t("accounting.period.banner.closedThrough", {
                      date: closedThroughLabel,
                    })}
              </p>
            )}
          </Field>

          <Field>
            <FormDate
              required
              label="invoices.fields.dueDate"
              name="dueDate"
              selected={dueDate}
              onChange={handleFieldChange(setdueDate, "dueDate")}
              dateFormat="dd/MM/yyyy"
              placeholderText={t("transactions.placeholders.date")}
              error={errors.dueDate}
            />
          </Field>

          {isPurchase && (
            <Field>
              <FormDate
                label="bills.fields.vatCreditPeriod"
                name="vatCreditPeriod"
                hint={t("bills.hints.vatCreditPeriod")}
                selected={
                  vatCreditPeriod ? fromPeriod(vatCreditPeriod) : null
                }
                onChange={(value) => {
                  setVatCreditPeriod(value ? toPeriod(value) : null);
                  clearError("vatCreditPeriod");
                }}
                onClear={() => {
                  setVatCreditPeriod(null);
                  clearError("vatCreditPeriod");
                }}
                showMonthYearPicker
                dateFormat="MMM yyyy"
                minDate={invoicePeriod ? fromPeriod(invoicePeriod) : undefined}
                maxDate={
                  maxCreditPeriod ? fromPeriod(maxCreditPeriod) : undefined
                }
                placeholderText={t("bills.placeholders.vatCreditPeriod")}
                error={errors.vatCreditPeriod}
              />
            </Field>
          )}

          {stockConsumedMessage && (
            <div data-testid="invoice-stock-consumed">
              <Alert
                type="warning"
                title={stockConsumedMessage}
                message="bills.errors.stockConsumedHint"
              />
            </div>
          )}

          <Field>
            <ProductEntryTable
              productItems={productItems}
              setProductItems={setProductItems}
              products={businessProducts}
              productLoading={businessProductsLoading}
              updateProductItem={updateProductItem}
              handleRemoveRow={handleRemoveRow}
              handleAddRow={handleAddRow}
              productItemsError={errors.productItems}
              businessProductsError={businessProductsError}
              taxes={availableTaxes}
              taxDirectory={taxDirectory}
              taxesLoading={businessTaxesLoading}
              taxesError={businessTaxesError}
              onRefresh={onLoad}
              isPurchase={isPurchase}
              subtotal={subtotal}
              totalDiscount={discountAmount}
              applyAfterTax={applyAfterTax}
              taxRounding={taxRounding}
            />
          </Field>

          <Field>
            <div className="flex flex-col space-y-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="subtract-stock"
                  checked={trackInventory}
                  disabled={isEdit}
                  onChange={(e) => {
                    setTrackInventory(e.target.checked);
                    if (!e.target.checked) {
                      setIgnoreStockValidation(false);
                    }
                  }}
                  className={`h-4 w-4 border-gray-300 rounded ${
                    isEdit
                      ? "opacity-60 cursor-not-allowed bg-gray-200"
                      : "text-blue-600 focus:ring-blue-500"
                  }`}
                />
                <label
                  htmlFor="subtract-stock"
                  className={`ml-2 block text-sm ${
                    isEdit ? "text-gray-500" : "text-gray-700"
                  }`}
                >
                  {t(
                    isPurchase
                      ? "bills.table.headers.addStock"
                      : "invoices.table.headers.subtractStock",
                  )}
                </label>
              </div>

              {!isEdit && !isPurchase && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="ignore-stock-validation"
                    checked={ignoreStockValidation}
                    disabled={!trackInventory}
                    onChange={(e) => setIgnoreStockValidation(e.target.checked)}
                    className={`h-4 w-4 border-gray-300 rounded ${
                      !trackInventory
                        ? "opacity-60 cursor-not-allowed bg-gray-200"
                        : "text-blue-600 focus:ring-blue-500"
                    }`}
                  />
                  <label
                    htmlFor="ignore-stock-validation"
                    className={`ml-2 block text-sm ${
                      !trackInventory ? "text-gray-500" : "text-gray-700"
                    }`}
                  >
                    {t("invoices.table.headers.ignoreStockValidation")}
                  </label>
                </div>
              )}
            </div>
          </Field>

          <Divider />

          <Field className="flex flex-col">
            <FormLabel>{t("invoices.fields.discount")}</FormLabel>
            <div className="mt-1 flex justify-between text-sm gap-4">
              <div className="flex-1">
                <FormSelect
                  id="discount"
                  name="discount"
                  options={DISCOUNT_OPTIONS}
                  value={selectedDiscount}
                  onChange={onSelectDiscont}
                  placeholder={t("invoices.placeholders.discount")}
                />
              </div>
              <div className="flex-1">
                <FormInput
                  id="amount"
                  name="amount"
                  value={discount}
                  disabled={
                    !selectedDiscount ||
                    selectedDiscount?.value === "noDiscount"
                  }
                  onChange={handleChangeDiscount}
                  onClear={() => setDiscount("")}
                  placeholder={t("invoices.placeholders.amount")}
                  numberFormat={{
                    thousandSeparator:
                      selectedDiscount?.value === "fixed" ? true : false,
                  }}
                  prefix={
                    selectedDiscount?.value === "fixed"
                      ? "Rp "
                      : "percentage"
                        ? "%"
                        : null
                  }
                  error={errors.discount}
                />
              </div>
            </div>
          </Field>

          <Divider />

          <Field>
            <div className="flex flex-col text-sm">
              <div className="flex justify-between">
                <span>{t("invoices.fields.subtotal")}</span>
                <div className="font-medium">
                  {currencyFormat(subtotal || 0)}
                </div>
              </div>

              {discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{t("invoices.fields.discount")}</span>
                  <div className="font-medium">
                    -{currencyFormat(discountAmount || 0)}
                  </div>
                </div>
              )}

              {taxes.map((item) => {
                // An unresolved tax is still charged, so the row needs a name
                // even when getAllTaxes could not read one. A withheld tax is
                // cut by the counterparty, so it reads as a deduction.
                const row = describeTaxRow(item, t);
                return (
                  <div
                    key={item.id}
                    className={clsx(
                      "flex justify-between",
                      row.withholding && "text-amber-700",
                    )}
                  >
                    <span>{row.label}</span>
                    <div className="font-medium">
                      {currencyFormat(row.value)}
                    </div>
                  </div>
                );
              })}

              {roundingAmount ? (
                <div className="flex justify-between">
                  <span>{t("invoices.fields.rounding")}</span>
                  <div className="font-medium">
                    {currencyFormat(roundingAmount)}
                  </div>
                </div>
              ) : null}
            </div>
          </Field>

          {depositBoxShown && (
            <Field>
              <div
                className="space-y-3 rounded-md border border-purple-200 bg-purple-50 p-3"
                data-testid="invoice-deposit-box"
              >
                <div className="flex items-center gap-2">
                  <input
                    id="invoice-use-deposits"
                    type="checkbox"
                    checked={useDeposits}
                    onChange={(e) => setUseDeposits(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="invoice-use-deposits"
                    className="text-sm font-medium text-gray-900"
                  >
                    {t("deposits.invoiceForm.toggle")}
                  </label>
                </div>
                {useDeposits && (
                  <>
                    <DepositAllocationList
                      deposits={partnerDeposits}
                      allocations={depositAllocations}
                      onChange={(next) => {
                        setDepositsTouched(true);
                        setDepositAllocations(next);
                      }}
                      limit={depositLimit}
                      errors={showDepositErrors ? depositErrors : {}}
                      idPrefix="invoice-deposit"
                    />
                    {showDepositErrors && depositErrors.total && (
                      <p className="text-sm text-red-600">
                        {t(depositErrors.total)}
                      </p>
                    )}
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>{t("deposits.invoiceForm.used")}</span>
                        <span className="font-medium tabular-nums">
                          −{formatCurrency(depositUsed)}
                        </span>
                      </div>
                      <div className="flex justify-between font-semibold">
                        <span>{t("deposits.invoiceForm.afterDeposit")}</span>
                        <span className="tabular-nums" data-testid="invoice-balance-after-deposit">
                          {formatCurrency(Math.max(0, depositLimit - depositUsed))}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Field>
          )}
        </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
    {showInitPrompt && (
      <InitAccountPromptModal
        open={showInitPrompt}
        businessID={user?.mainBusiness?.id}
        onSkip={() => setInitSkipped(true)}
      />
    )}
    </>
  );
};

export default InvoiceModal;
